// Barrel module for editor command builders. Implementation lives in `cmd/*.ts`,
// split by domain (graph / scene / library). The `EditorCommand` union and
// merge rules stay here so consumers have a single import surface.

export type {
  EdgeRef,
  CreateNodeCmd,
  DeleteNodeCmd,
  MoveNodeCmd,
  SetNodeDataCmd,
  ReplaceNodeCmd,
  ConnectCmd,
  ReconnectEdgeCmd,
  CutEdgesCmd,
  SetParamValueCmd,
} from './cmd/graph.js';

export {
  createNodeCommand,
  createMoveNodeCommand,
  createDeleteNodeCommand,
  createSetNodeDataCommand,
  createReplaceNodeCommand,
  createConnectCommand,
  createReconnectEdgeCommand,
  createSetParamValueCommand,
  createCutEdgesCommand,
} from './cmd/graph.js';

export type { SetRuntimeGraphCmd, SetDataOwnerCmd, SetDataSourcesCmd, SetShapesCmd, SetRuntimeModelCmd, SetRuntimeStateCmd, SetSceneEventsCmd, SetSceneDataContextCmd } from './cmd/data.js';
export { createSetRuntimeGraphCommand, createSetDataOwnerCommand, createSetDataSourcesCommand, createSetShapesCommand, createSetRuntimeModelCommand, createSetRuntimeStateCommand, createSetSceneEventsCommand, createSetSceneDataContextCommand } from './cmd/data.js';

export type {
  AddFrameCmd,
  AddLayerCmd,
  AddInstanceCmd,
  AddExistingInstanceCmd,
  DeleteSceneNodeCmd,
  FrameEditableProps,
  InstanceEditableProps,
  SetFramePropertyCmd,
  SetInstancePropertyCmd,
  SetNodeParamOverrideCmd,
  RenameSceneNodeCmd,
  RenameComponentCmd,
  ClearAllCmd,
  ValueComponent,
  ValueRangeField,
} from './cmd/scene.js';

export {
  createAddFrameCommand,
  createAddLayerCommand,
  createAddInstanceCommand,
  createAddExistingInstanceCommand,
  createDeleteSceneNodeCommand,
  createSetFramePropertyCommand,
  createSetInstancePropertyCommand,
  createSetNodeParamOverrideCommand,
  createRenameSceneNodeCommand,
  createRenameComponentCommand,
  createClearCommand,
} from './cmd/scene.js';

export type {
  CreateLibraryItemCmd,
  SaveToLibraryCmd,
  DeleteLibraryItemCmd,
  AttachLibraryCmd,
  DetachLibraryCmd,
} from './cmd/library.js';

export {
  createLibraryItemCommand,
  createSaveToLibraryCommand,
  createAttachLibraryCommand,
  createDetachLibraryCommand,
} from './cmd/library.js';

import type {
  CreateNodeCmd,
  DeleteNodeCmd,
  MoveNodeCmd,
  SetNodeDataCmd,
  ConnectCmd,
  ReconnectEdgeCmd,
  CutEdgesCmd,
  SetParamValueCmd,
} from './cmd/graph.js';
import type { SetRuntimeGraphCmd, SetDataOwnerCmd, SetRuntimeStateCmd, SetSceneEventsCmd, SetSceneDataContextCmd } from './cmd/data.js';
import type {
  AddFrameCmd,
  AddLayerCmd,
  AddInstanceCmd,
  AddExistingInstanceCmd,
  DeleteSceneNodeCmd,
  SetFramePropertyCmd,
  SetInstancePropertyCmd,
  SetNodeParamOverrideCmd,
  RenameSceneNodeCmd,
  RenameComponentCmd,
  ClearAllCmd,
} from './cmd/scene.js';
import type {
  CreateLibraryItemCmd,
  SaveToLibraryCmd,
  DeleteLibraryItemCmd,
  AttachLibraryCmd,
  DetachLibraryCmd,
} from './cmd/library.js';

export type EditorCommand =
  | CreateNodeCmd
  | DeleteNodeCmd
  | MoveNodeCmd
  | SetNodeDataCmd
  | import('./cmd/graph.js').ReplaceNodeCmd
  | ConnectCmd
  | ReconnectEdgeCmd
  | CutEdgesCmd
  | SetParamValueCmd
  | AddFrameCmd
  | AddLayerCmd
  | AddInstanceCmd
  | AddExistingInstanceCmd
  | DeleteSceneNodeCmd
  | SetFramePropertyCmd
  | SetInstancePropertyCmd
  | SetNodeParamOverrideCmd
  | RenameSceneNodeCmd
  | RenameComponentCmd
  | ClearAllCmd
  | CreateLibraryItemCmd
  | SaveToLibraryCmd
  | DeleteLibraryItemCmd
  | AttachLibraryCmd
  | DetachLibraryCmd
  | SetRuntimeGraphCmd
  | SetDataOwnerCmd
  | SetSceneDataContextCmd
  | import('./cmd/data.js').SetDataSourcesCmd
  | import('./cmd/data.js').SetShapesCmd
  | SetRuntimeStateCmd
  | import('./cmd/data.js').SetRuntimeModelCmd
  | SetSceneEventsCmd;

type MergeFn<T extends EditorCommand> = (prev: T, next: T) => T | null;

export const COMMAND_MERGE: { [K in EditorCommand['type']]?: MergeFn<Extract<EditorCommand, { type: K }>> } = {
  'params:setValue': (prev, next) => prev.key === next.key ? next : null,
  'graph:moveNode': (prev, next) => prev.nodeId === next.nodeId ? next : null,
  'graph:setData': (prev, next) => prev.nodeId === next.nodeId && prev.field === next.field ? next : null,
  'scene:setFrameProperty': (prev, next) => prev.nodeId === next.nodeId && prev.prop === next.prop ? next : null,
  'scene:setInstanceProperty': (prev, next) => prev.nodeId === next.nodeId && prev.prop === next.prop ? next : null,
  'scene:setNodeParamOverride': (prev, next) =>
    prev.nodeId === next.nodeId && prev.key === next.key ? next : null,
  'scene:rename': (prev, next) => prev.nodeId === next.nodeId ? next : null,
};
